import React, { useCallback, useEffect } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { PaymentMethodAddNewItemOption } from 'components/payment-method-add-new-item-option';
import PaymentMethodOption from 'components/payment-method-option';
import { usePaycometPaypalPopup } from 'hooks/use-paycomet-paypal-popup';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useMParticleContext } from 'state/mParticle';
import noop from 'utils/noop';
import { relativeRoutes } from 'utils/routing';

import { ADD_PREPAID_CARD } from '../constants';
import {
  StyledAddNewCreditCardIcon,
  StyledChequeGourmetCardIcon,
  StyledIdealCardIcon,
  StyledPaypalCardIcon,
  StyledPrepaidCardIcon,
  StyledSodexoCardIcon,
} from '../styled';
import sortPaymentMethods from '../utils/sort-payment-methods';

import { PaymentMethodOptionItemDivider, StyledOption } from './styled';
import { PaymentMethodOptionProps } from './types';

function PaymentMethodOptions({
  collapsed,
  setCollapsed,
  unSelectedMethods,
  handleSelect,
  setIsAddNewCardOptionSelected,
  setIsAddNewGiftCardOptionSelected,
  setIsPaycometPaypalOptionSelected,
  setIsAdyenIdealOptionSelected,
  setIsPaycometSodexoOptionSelected,
  setIsPaycometChequeGourmetOptionSelected,
  showAddNewGiftCardOption,
  showAddNewCardOption,
  showAdyenIdealPaymentOption,
  onResult = noop,
  serverOrder = null,
}: PaymentMethodOptionProps) {
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { logAddPaymentMethodClick } = useMParticleContext();
  const enableGiftCard = useFlag(LaunchDarklyFlag.ENABLE_GIFT_CARD);
  const { openPaycometPaypalPopup, isLoadingPopup, isPopupOpen } = usePaycometPaypalPopup(
    onResult,
    serverOrder
  );
  const enablePayPalPaycomet = useFlag(LaunchDarklyFlag.ENABLE_PAYPAL_PAYCOMET);
  const enableSodexoCreditCardPaycomet = useFlag(
    LaunchDarklyFlag.ENABLE_SODEXO_CREDIT_CARD_PAYCOMET
  );
  const enableChequeGourmetCreditCardPaycomet = useFlag(
    LaunchDarklyFlag.ENABLE_CHEQUE_GOURMET_CREDIT_CARD_PAYCOMET
  );
  const enableSortPaymentMethods = useFlag(LaunchDarklyFlag.ENABLE_SORT_PAYMENT_METHODS);

  const handleUnselectedMethodClick = useCallback(
    newfdAccountId => {
      setIsAddNewCardOptionSelected(false);
      setIsAddNewGiftCardOptionSelected(false);
      if (setIsPaycometPaypalOptionSelected) {
        setIsPaycometPaypalOptionSelected(false);
      }
      if (setIsAdyenIdealOptionSelected) {
        setIsAdyenIdealOptionSelected(false);
      }
      if (setIsPaycometSodexoOptionSelected) {
        setIsPaycometSodexoOptionSelected('');
      }
      if (setIsPaycometChequeGourmetOptionSelected) {
        setIsPaycometChequeGourmetOptionSelected('');
      }
      handleSelect(newfdAccountId);
      setCollapsed(true);
    },
    [
      setIsAddNewCardOptionSelected,
      setIsAddNewGiftCardOptionSelected,
      setIsPaycometPaypalOptionSelected,
      setIsPaycometSodexoOptionSelected,
      setIsPaycometChequeGourmetOptionSelected,
      setIsAdyenIdealOptionSelected,
      handleSelect,
      setCollapsed,
    ]
  );

  const handleAddNewCardOptionClick = useCallback(
    (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      logAddPaymentMethodClick();
      setIsAddNewCardOptionSelected(true);
      setIsAddNewGiftCardOptionSelected(false);
      if (setIsPaycometPaypalOptionSelected) {
        setIsPaycometPaypalOptionSelected(false);
      }
      if (setIsAdyenIdealOptionSelected) {
        setIsAdyenIdealOptionSelected(false);
      }
      if (setIsPaycometSodexoOptionSelected) {
        setIsPaycometSodexoOptionSelected('');
      }
      if (setIsPaycometChequeGourmetOptionSelected) {
        setIsPaycometChequeGourmetOptionSelected('');
      }
      handleSelect();
      setCollapsed(true);
    },
    [
      logAddPaymentMethodClick,
      setIsAddNewCardOptionSelected,
      setIsAddNewGiftCardOptionSelected,
      setIsPaycometPaypalOptionSelected,
      setIsPaycometSodexoOptionSelected,
      setIsPaycometChequeGourmetOptionSelected,
      setIsAdyenIdealOptionSelected,
      handleSelect,
      setCollapsed,
    ]
  );

  const handleAddNewGiftCardOptionClick = useCallback(
    (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setIsAddNewGiftCardOptionSelected(true);
      setIsAddNewCardOptionSelected(false);
      if (setIsPaycometPaypalOptionSelected) {
        setIsPaycometPaypalOptionSelected(false);
      }
      if (setIsAdyenIdealOptionSelected) {
        setIsAdyenIdealOptionSelected(false);
      }
      if (setIsPaycometSodexoOptionSelected) {
        setIsPaycometSodexoOptionSelected('');
      }
      if (setIsPaycometChequeGourmetOptionSelected) {
        setIsPaycometChequeGourmetOptionSelected('');
      }
      handleSelect();
      setCollapsed(true);
      navigate(relativeRoutes.addGiftCard);
    },
    [
      setIsAddNewGiftCardOptionSelected,
      setIsAddNewCardOptionSelected,
      setIsPaycometPaypalOptionSelected,
      setIsPaycometSodexoOptionSelected,
      setIsPaycometChequeGourmetOptionSelected,
      setIsAdyenIdealOptionSelected,
      handleSelect,
      setCollapsed,
      navigate,
    ]
  );

  const handlePaypalOptionClick = useCallback(
    (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();

      setIsAddNewCardOptionSelected(false);
      setIsAddNewGiftCardOptionSelected(false);
      if (setIsPaycometPaypalOptionSelected) {
        setIsPaycometPaypalOptionSelected(true);
      }
      if (setIsAdyenIdealOptionSelected) {
        setIsAdyenIdealOptionSelected(false);
      }
      if (setIsPaycometSodexoOptionSelected) {
        setIsPaycometSodexoOptionSelected('');
      }
      if (setIsPaycometChequeGourmetOptionSelected) {
        setIsPaycometChequeGourmetOptionSelected('');
      }

      openPaycometPaypalPopup();
    },
    [
      setIsAddNewCardOptionSelected,
      setIsAddNewGiftCardOptionSelected,
      setIsPaycometPaypalOptionSelected,
      setIsPaycometSodexoOptionSelected,
      setIsPaycometChequeGourmetOptionSelected,
      setIsAdyenIdealOptionSelected,
      openPaycometPaypalPopup,
    ]
  );

  const handleAdyenIdealOptionClick = useCallback(
    (e: React.FormEvent<HTMLButtonElement>) => {
      e.preventDefault();
      setIsAddNewCardOptionSelected(false);
      setIsAddNewGiftCardOptionSelected(false);
      if (setIsPaycometPaypalOptionSelected) {
        setIsPaycometPaypalOptionSelected(false);
      }
      if (setIsAdyenIdealOptionSelected) {
        setIsAdyenIdealOptionSelected(true);
      }
      if (setIsPaycometSodexoOptionSelected) {
        setIsPaycometSodexoOptionSelected('');
      }
      if (setIsPaycometChequeGourmetOptionSelected) {
        setIsPaycometChequeGourmetOptionSelected('');
      }

      handleSelect();
      setCollapsed(true);
    },
    [
      setIsAddNewCardOptionSelected,
      setIsAdyenIdealOptionSelected,
      setIsAddNewGiftCardOptionSelected,
      setIsPaycometPaypalOptionSelected,
      setIsPaycometSodexoOptionSelected,
      setIsPaycometChequeGourmetOptionSelected,
      handleSelect,
      setCollapsed,
    ]
  );

  const handleSodexoOptionClick = useCallback(
    (e: React.FormEvent<HTMLButtonElement>, sodexoType: string) => {
      e.preventDefault();
      setIsAddNewCardOptionSelected(false);
      setIsAddNewGiftCardOptionSelected(false);
      if (setIsPaycometPaypalOptionSelected) {
        setIsPaycometPaypalOptionSelected(false);
      }
      if (setIsAdyenIdealOptionSelected) {
        setIsAdyenIdealOptionSelected(false);
      }
      if (setIsPaycometSodexoOptionSelected) {
        setIsPaycometSodexoOptionSelected(sodexoType);
        if (sodexoType === 'card') {
          setIsAddNewCardOptionSelected(true);
        }
      }
      if (setIsPaycometChequeGourmetOptionSelected) {
        setIsPaycometChequeGourmetOptionSelected('');
      }

      handleSelect();
      setCollapsed(true);
    },
    [
      setIsAddNewCardOptionSelected,
      setIsAdyenIdealOptionSelected,
      setIsAddNewGiftCardOptionSelected,
      setIsPaycometPaypalOptionSelected,
      setIsPaycometSodexoOptionSelected,
      setIsPaycometChequeGourmetOptionSelected,
      handleSelect,
      setCollapsed,
    ]
  );

  const handleChequeGourmetOptionClick = useCallback(
    (e: React.FormEvent<HTMLButtonElement>, chequeGourmetType: string) => {
      e.preventDefault();
      setIsAddNewCardOptionSelected(false);
      setIsAddNewGiftCardOptionSelected(false);
      if (setIsPaycometPaypalOptionSelected) {
        setIsPaycometPaypalOptionSelected(false);
      }
      if (setIsAdyenIdealOptionSelected) {
        setIsAdyenIdealOptionSelected(false);
      }
      if (setIsPaycometSodexoOptionSelected) {
        setIsPaycometSodexoOptionSelected('');
      }
      if (setIsPaycometChequeGourmetOptionSelected) {
        setIsPaycometChequeGourmetOptionSelected(chequeGourmetType);
        if (chequeGourmetType === 'card') {
          setIsAddNewCardOptionSelected(true);
        }
      }

      handleSelect();
      setCollapsed(true);
    },
    [
      setIsAddNewCardOptionSelected,
      setIsAdyenIdealOptionSelected,
      setIsAddNewGiftCardOptionSelected,
      setIsPaycometPaypalOptionSelected,
      setIsPaycometSodexoOptionSelected,
      setIsPaycometChequeGourmetOptionSelected,
      handleSelect,
      setCollapsed,
    ]
  );

  useEffect(() => {
    if (!isLoadingPopup && isPopupOpen) {
      handleSelect();
      setCollapsed(true);
    }
  }, [handleSelect, isPopupOpen, isLoadingPopup, setCollapsed]);

  if (collapsed) {
    return null;
  }

  if (enableSortPaymentMethods) {
    unSelectedMethods = sortPaymentMethods(unSelectedMethods);
  }

  return (
    <>
      {!enableSortPaymentMethods &&
        unSelectedMethods.map(method => (
          <StyledOption key={method.accountIdentifier}>
            <PaymentMethodOptionItemDivider />
            <PaymentMethodOption
              method={method}
              onClick={handleUnselectedMethodClick}
              disableMethod={!!method.prepaid && !enableGiftCard}
              hideBorder
            />
          </StyledOption>
        ))}

      {enableSortPaymentMethods &&
        unSelectedMethods.map((method, index) => (
          <div key={index}>
            {!method?.transient && (
              <StyledOption key={method.accountIdentifier}>
                <PaymentMethodOptionItemDivider />
                <PaymentMethodOption
                  method={method}
                  onClick={handleUnselectedMethodClick}
                  disableMethod={!!method.prepaid && !enableGiftCard}
                  hideBorder
                />
              </StyledOption>
            )}

            {method?.sodexo && method?.transient && (
              <StyledOption>
                <PaymentMethodOptionItemDivider />
                <PaymentMethodAddNewItemOption
                  data-testid="add-sodexo-credit-card-payment"
                  text={formatMessage({ id: 'addNewPaymentSodexo' })}
                  Icon={<StyledSodexoCardIcon />}
                  onClick={e => handleSodexoOptionClick(e, 'card')}
                  isLoading={isLoadingPopup}
                />
              </StyledOption>
            )}

            {method?.chequeGourmet && method?.transient && (
              <StyledOption>
                <PaymentMethodOptionItemDivider />
                <PaymentMethodAddNewItemOption
                  data-testid="add-cheque-gourmet-credit-card-payment"
                  text={formatMessage({ id: 'addNewPaymentChequeGourmet' })}
                  Icon={<StyledChequeGourmetCardIcon />}
                  onClick={e => handleChequeGourmetOptionClick(e, 'card')}
                  isLoading={isLoadingPopup}
                />
              </StyledOption>
            )}

            {method?.paypal && method?.transient && (
              <StyledOption>
                <PaymentMethodOptionItemDivider />
                <PaymentMethodAddNewItemOption
                  data-testid="add-paypal-payment"
                  text={formatMessage({ id: 'paypal' })}
                  Icon={<StyledPaypalCardIcon />}
                  onClick={handlePaypalOptionClick}
                  isLoading={isLoadingPopup}
                />
              </StyledOption>
            )}
          </div>
        ))}

      {showAdyenIdealPaymentOption && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            data-testid="pay-via-adyen-ideal"
            text={formatMessage({ id: 'payViaIdeal' })}
            Icon={<StyledIdealCardIcon />}
            onClick={handleAdyenIdealOptionClick}
          />
        </StyledOption>
      )}

      {showAddNewGiftCardOption && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            data-testid="add-gift-card"
            text={formatMessage({ id: ADD_PREPAID_CARD })}
            Icon={<StyledPrepaidCardIcon />}
            onClick={handleAddNewGiftCardOptionClick}
          />
        </StyledOption>
      )}

      {!enableSortPaymentMethods && enableSodexoCreditCardPaycomet && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            data-testid="add-sodexo-credit-card-payment"
            text={formatMessage({ id: 'addNewPaymentSodexo' })}
            Icon={<StyledSodexoCardIcon />}
            onClick={e => handleSodexoOptionClick(e, 'card')}
            isLoading={isLoadingPopup}
          />
        </StyledOption>
      )}

      {!enableSortPaymentMethods && enableChequeGourmetCreditCardPaycomet && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            data-testid="add-cheque-gourmet-credit-card-payment"
            text={formatMessage({ id: 'addNewPaymentChequeGourmet' })}
            Icon={<StyledChequeGourmetCardIcon />}
            onClick={e => handleChequeGourmetOptionClick(e, 'card')}
            isLoading={isLoadingPopup}
          />
        </StyledOption>
      )}

      {!enableSortPaymentMethods && enablePayPalPaycomet && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            data-testid="add-paypal-payment"
            text={formatMessage({ id: 'paypal' })}
            Icon={<StyledPaypalCardIcon />}
            onClick={handlePaypalOptionClick}
            isLoading={isLoadingPopup}
          />
        </StyledOption>
      )}

      {showAddNewCardOption && (
        <StyledOption>
          <PaymentMethodOptionItemDivider />
          <PaymentMethodAddNewItemOption
            data-testid="add-new-card"
            text={formatMessage({ id: 'addNewCard' })}
            Icon={<StyledAddNewCreditCardIcon />}
            onClick={handleAddNewCardOptionClick}
          />
        </StyledOption>
      )}
    </>
  );
}

export default PaymentMethodOptions;
